<template>
  <b-card
    no-body
    class="no-padding"
  >
    <div class="section-block m-2">
      <div class="site-hardware-actions site-hardware-actions_management mb-2">
        <div class="section-block__title mb-0">
          <h2>Back To Home VPN</h2>
        </div>
        <div
          v-if="canManage && status && status.is_running && status.has_public_ip"
          class="double-button"
        >
          <b-button
            variant="primary"
            @click="openCreate"
          >
            <feather-icon
              icon="PlusIcon"
              width="16"
              height="16"
              class="mr-25"
            />
            Add User
          </b-button>
        </div>
      </div>

      <StatusCard
        :status="status"
        :can-toggle="canToggle"
        :toggling="isToggling"
        @toggle="handleToggle"
      />

      <template v-if="status && status.is_enabled">
        <table-mobile-solo
          :handle-search="null"
          :items="rows"
          :loading="isUsersLoading"
          :fields="mobileFields"
          :rules="mobileRules"
          :actions="mobileActions"
          custom-class="no-padding"
          key-value="name"
          key-prop="id"
        />
        <b-card-body class="px-0">
          <vue-good-table
            :is-loading="isUsersLoading"
            :columns="fields"
            :rows="rows"
            compact-mode
            class="vgt-unset-overflow hide-mobile-table"
          >
            <template slot="loadingContent">
              <b-spinner
                variant="primary"
                label="Spinning"
              />
            </template>
            <template slot="emptystate">
              <div class="text-center">
                {{ status.is_running ? 'No VPN users yet.' : 'Back To Home is starting on the router.' }}
              </div>
            </template>
            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.field === 'name'">
                {{ props.row.name }}
                <b-badge
                  v-if="props.row.file_access !== 'disabled'"
                  v-b-tooltip.hover
                  title="File access is managed in WinBox or the Back To Home app"
                  pill
                  variant="light-secondary"
                  class="ml-50"
                >
                  Files: {{ props.row.file_access }}
                </b-badge>
              </span>
              <span v-else-if="props.column.field === 'state'">
                <b-badge
                  pill
                  :variant="stateVariants[props.row.state]"
                >
                  {{ stateLabels[props.row.state] }}
                </b-badge>
              </span>
              <span v-else-if="props.column.field === 'is_enabled'">
                <!-- The key re-renders the switch from the router state when a change fails. -->
                <b-form-checkbox
                  :key="`${props.row.id}-${props.row.is_enabled}-${switchRenderKey}`"
                  :checked="props.row.is_enabled"
                  switch
                  inline
                  :disabled="!canManage || busyUserId === props.row.id || !canEnable(props.row)"
                  @change="setEnabled(props.row, $event)"
                />
              </span>
              <span v-else-if="props.column.field === 'action'">
                <!-- Positioned against the window: with few rows the tab and table containers cut the menu off. -->
                <b-dropdown
                  v-if="canManage"
                  variant="link"
                  toggle-class="text-decoration-none"
                  boundary="window"
                  :popper-opts="{ positionFixed: true }"
                  no-caret
                >
                  <template v-slot:button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body align-middle mr-25"
                    />
                  </template>
                  <b-dropdown-item @click.prevent="openConfig(props.row)">
                    <feather-icon
                      icon="SmartphoneIcon"
                      class="mr-50"
                    />
                    <span>Config &amp; QR</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click.prevent="openEdit(props.row)">
                    <feather-icon
                      icon="Edit2Icon"
                      class="mr-50"
                    />
                    <span>Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="canEnable(props.row)"
                    @click.prevent="setEnabled(props.row, !props.row.is_enabled)"
                  >
                    <feather-icon
                      :icon="props.row.is_enabled ? 'PauseCircleIcon' : 'PlayCircleIcon'"
                      class="mr-50"
                    />
                    <span>{{ props.row.is_enabled ? 'Disable' : 'Enable' }}</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click.prevent="confirmRemove(props.row)">
                    <feather-icon
                      icon="TrashIcon"
                      class="mr-50"
                    />
                    <span>Remove</span>
                  </b-dropdown-item>
                </b-dropdown>
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </b-card-body>
      </template>
    </div>

    <UserModal
      ref="userModal"
      :site-id="siteId"
      :user="selectedUser"
    />
    <ConfigModal
      ref="configModal"
      :site-id="siteId"
    />
    <b-modal
      ref="disableModal"
      title="Disable Back To Home VPN?"
      centered
      ok-title="Disable"
      ok-variant="danger"
      @ok="disable"
    >
      <p>
        All VPN users of this site and their configs will be deleted.
      </p>
      <p class="mb-0">
        If Back To Home is enabled again, the router gets a new key and port, so every device has to be set up again with a new config.
      </p>
    </b-modal>
    <b-modal
      ref="removeModal"
      title="Remove this VPN user?"
      centered
      ok-title="Remove"
      ok-variant="danger"
      @ok="removeUser"
      @hidden="userToRemove = null"
    >
      <p class="mb-0">
        The config of {{ userToRemove ? userToRemove.name : '' }} will stop working for good.
        To give access back, add a new user and send them a new config. To pause access instead, disable the user.
      </p>
    </b-modal>
  </b-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import {
  BCard, BCardBody, BButton, BBadge, BDropdown, BDropdownItem, BFormCheckbox, BModal, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TableMobileSolo from '@/components/TableMobileSolo.vue'
import StatusCard from './StatusCard.vue'
import UserModal from './UserModal.vue'
import ConfigModal from './ConfigModal.vue'

const POLL_INTERVAL = 5000

// Starting usually takes seconds; three minutes covers a slow MikroTik cloud.
const POLL_ATTEMPTS = 36

const USERS_REFRESH_INTERVAL = 10000

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardBody,
    BButton,
    BBadge,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BModal,
    BSpinner,
    VueGoodTable,
    TableMobileSolo,
    StatusCard,
    UserModal,
    ConfigModal,
  },
  props: {
    siteId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isUsersLoading: false,
      isToggling: false,
      busyUserId: null,
      switchRenderKey: 0,
      selectedUser: null,
      userToRemove: null,
      pollTimer: null,
      pollAttempts: 0,
      usersRefreshTimer: null,
      isUsersRefreshing: false,
      stateLabels: {
        online: 'Online',
        offline: 'Offline',
        disabled: 'Disabled',
        expired: 'Expired',
      },
      stateVariants: {
        online: 'success',
        offline: 'secondary',
        disabled: 'warning',
        expired: 'danger',
      },
      fields: [
        {
          field: 'name',
          label: 'Name',
          sortable: false,
        },
        {
          field: 'comment',
          label: 'Comment',
          sortable: false,
        },
        {
          field: 'client_address',
          label: 'Client Address',
          sortable: false,
        },
        {
          field: 'allow_lan',
          label: 'LAN Access',
          sortable: false,
          formatFn: value => (value ? 'Yes' : 'No'),
        },
        {
          field: 'expires_at',
          label: 'Expires',
          sortable: false,
          formatFn: value => value || 'Never',
        },
        {
          field: 'state',
          label: 'Status',
          sortable: false,
        },
        {
          field: 'is_enabled',
          label: 'Enabled',
          sortable: false,
        },
        {
          field: 'action',
          label: 'Actions',
          sortable: false,
          thClass: 'action-th-width',
          hidden: !this.$can('update', 'sites'),
        },
      ],
    }
  },
  computed: {
    ...mapState('backToHome', ['status', 'users']),
    canManage() {
      return this.$can('update', 'sites')
    },
    canToggle() {
      return this.$can('controller-action', 'sites')
    },
    rows() {
      return this.users.map(user => ({
        ...user,
        client_address: user.client_addresses[0] || '-',
        state: user.status,
      }))
    },
    mobileFields() {
      return this.fields.filter(field => field.field !== 'is_enabled' && (field.field !== 'action' || this.canManage))
    },
    mobileRules() {
      return {
        allow_lan: value => (value ? 'Yes' : 'No'),
        expires_at: value => value || 'Never',
        state: value => this.stateLabels[value],
      }
    },
    mobileActions() {
      return [{
        label: 'Config & QR',
        type: 'primary',
        visible: () => this.canManage,
        click: user => this.openConfig(user),
      }, {
        label: 'Edit',
        type: 'primary',
        visible: () => this.canManage,
        click: user => this.openEdit(user),
      }, {
        label: user => (user.is_enabled ? 'Disable' : 'Enable'),
        type: 'primary',
        visible: user => this.canManage && this.canEnable(user),
        click: user => this.setEnabled(user, !user.is_enabled),
      }, {
        label: 'Remove',
        type: 'danger',
        visible: () => this.canManage,
        click: user => this.confirmRemove(user),
      }]
    },
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopUsersRefresh()
    this.RESET_STATE()
  },
  methods: {
    ...mapActions('backToHome', {
      fetchStatus: 'getStatus',
      fetchUsers: 'getUsers',
      enableBackToHome: 'enable',
      disableBackToHome: 'disable',
      setUserEnabled: 'setUserEnabled',
      deleteUser: 'deleteUser',
    }),
    ...mapMutations('backToHome', ['RESET_STATE']),
    async load() {
      try {
        this.applyStatus(await this.fetchStatus(this.siteId))
      } catch (error) {
        console.warn(error)
      }
    },
    applyStatus(status) {
      if (status.is_enabled && !status.is_running) {
        this.startPolling()
      } else {
        this.stopPolling()
      }

      if (status.is_running) {
        this.loadUsers()
        this.startUsersRefresh()
      } else {
        this.stopUsersRefresh()
      }
    },
    async loadUsers() {
      this.isUsersLoading = true

      try {
        await this.fetchUsers(this.siteId)
      } catch (error) {
        console.warn(error)
      } finally {
        this.isUsersLoading = false
      }
    },
    startUsersRefresh() {
      if (this.usersRefreshTimer) {
        return
      }

      this.usersRefreshTimer = setInterval(this.refreshUsers, USERS_REFRESH_INTERVAL)
    },
    stopUsersRefresh() {
      clearInterval(this.usersRefreshTimer)
      this.usersRefreshTimer = null
    },
    // Refreshes the online status in the background: no table spinner, and nothing while the user is busy.
    async refreshUsers() {
      if (document.hidden || this.isUsersLoading || this.isUsersRefreshing || this.busyUserId) {
        return
      }

      this.isUsersRefreshing = true

      try {
        await this.fetchUsers(this.siteId)
      } catch (error) {
        // Stops repeating the error toast every interval, e.g. while the controller is offline.
        this.stopUsersRefresh()
      } finally {
        this.isUsersRefreshing = false
      }
    },
    startPolling() {
      if (this.pollTimer) {
        return
      }

      this.pollAttempts = 0
      this.pollTimer = setInterval(this.poll, POLL_INTERVAL)
    },
    stopPolling() {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    async poll() {
      this.pollAttempts += 1

      try {
        const status = await this.fetchStatus(this.siteId)

        if (!status.is_enabled || status.is_running || this.pollAttempts >= POLL_ATTEMPTS) {
          this.applyStatus(status)
          this.stopPolling()
        }
      } catch (error) {
        this.stopPolling()
      }
    },
    handleToggle(isEnabled) {
      if (isEnabled) {
        this.enable()
      } else {
        this.$refs.disableModal.show()
      }
    },
    async enable() {
      this.isToggling = true

      try {
        this.applyStatus(await this.enableBackToHome(this.siteId))
        this.notify('Back To Home VPN enabled')
      } catch (error) {
        console.warn(error)
      } finally {
        this.isToggling = false
      }
    },
    async disable() {
      this.isToggling = true

      try {
        this.applyStatus(await this.disableBackToHome(this.siteId))
        this.notify('Back To Home VPN disabled')
      } catch (error) {
        console.warn(error)
      } finally {
        this.isToggling = false
      }
    },
    // Without Public IP the users are paused, so they can be disabled but not enabled.
    canEnable(user) {
      return user.is_enabled || this.status.has_public_ip
    },
    openCreate() {
      this.selectedUser = null
      this.$nextTick(() => this.$refs.userModal.show())
    },
    openEdit(user) {
      this.selectedUser = user
      this.$nextTick(() => this.$refs.userModal.show())
    },
    openConfig(user) {
      this.$refs.configModal.open(user)
    },
    async setEnabled(user, isEnabled) {
      this.busyUserId = user.id

      try {
        await this.setUserEnabled({ siteId: this.siteId, id: user.id, isEnabled })
        this.notify(isEnabled ? 'VPN user enabled' : 'VPN user disabled')
      } catch (error) {
        this.switchRenderKey += 1
      } finally {
        this.busyUserId = null
      }
    },
    confirmRemove(user) {
      this.userToRemove = user
      this.$refs.removeModal.show()
    },
    async removeUser() {
      try {
        await this.deleteUser({ siteId: this.siteId, id: this.userToRemove.id })
        this.notify('VPN user removed')
      } catch (error) {
        console.warn(error)
      }
    },
    notify(title) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'BellIcon',
          variant: 'success',
        },
      })
    },
  },
}
</script>
